import type { CSSProperties } from "react";
import type { AboTermin } from "../../hooks/useKalenderAbos";
import { AboTerminListe } from "./AboTerminListe";
import { BuerotagKnopf, NichtDaKnopf, SperrZeilen } from "./SperrZeilen";
import type { Sperre } from "../../hooks/useSperrzeiten";
import { slotStundenNetto } from "../../lib/planer";
import type { Termin } from "../../lib/types";
import { MONTHS, WD_LONG, colorFor, h1, hhmm, istBesichtigung } from "./planer-utils";

/**
 * Der gewählte Tag unter dem Monatsraster — nur auf dem Telefon (kalenderabo.css).
 *
 * Wunsch vom 21.09.2026: Der Tipp auf einen Tag öffnet kein Blatt mehr, sondern
 * zeigt die Termine darunter, wie in Syncus. Erst der Tipp auf einen Termin
 * öffnet dessen Einzelheiten über den ganzen Bildschirm. Darunter stehen die
 * Termine der abonnierten Kalender — nur zum Lesen.
 */
export function TagesAnsicht({ dateIso, termine, abo, sperren, onTermin, onAbo, onNeu, onAufnehmen, onSperre, onNichtDa, onBuerotag }: {
  dateIso: string;
  termine: Termin[];
  abo: { termine: AboTermin[]; fehler: { kalender: string; text: string }[]; hatAbos: boolean; laedt: boolean };
  onTermin: (t: Termin) => void;
  onAbo: (t: AboTermin) => void;
  onNeu: () => void;
  onAufnehmen: (t: Termin, modus: "verschieben" | "kopieren") => void;
  /** Wer an dem Tag nicht da ist — steht ganz oben (16.10.2026). */
  sperren: Sperre[];
  onSperre: (s: Sperre) => void;
  onNichtDa: () => void;
  /** Bürotag eintragen (20.10.2026). */
  onBuerotag: () => void;
}) {
  const d = new Date(`${dateIso}T00:00:00`);
  const anzahl = termine.length + abo.termine.length;

  return (
    <section className="pl-tag" aria-label="Termine des gewählten Tages">
      <div className="pl-tag-kopf">
        <div className="pl-tag-titel">
          {WD_LONG[d.getDay()]}, {d.getDate()}. {MONTHS[d.getMonth()]}
          <div className="pl-tag-sub">{anzahl === 0 ? "keine Termine" : `${anzahl} ${anzahl === 1 ? "Termin" : "Termine"}`}</div>
        </div>
        <div className="sz-tag-knoepfe">
          <BuerotagKnopf onClick={onBuerotag} />
          <NichtDaKnopf onClick={onNichtDa} />
          <button type="button" className="pl-tag-neu" onClick={onNeu}>＋ Termin</button>
        </div>
      </div>

      <SperrZeilen tag={dateIso} sperren={sperren} onSperre={onSperre} />

      <div className="pl-tag-liste">
        {termine.map((t) => {
          const farbe = t.farbe && /^#/.test(t.farbe) ? t.farbe : colorFor(t.projekt_id || t.id, "helfer");
          const std = t.slots.reduce((a, sl) => a + slotStundenNetto(sl), 0);
          const leute = t.slots.length === 0 ? "keine Einsätze" : `${t.slots.length} ${t.slots.length === 1 ? "Person" : "Personen"} · ${h1(std)} h`;
          return (
            <div key={t.id} className="pl-tag-eintrag">
              <button type="button" className="pl-tag-haupt" onClick={() => onTermin(t)}>
                <span className="pl-tag-balken" style={{ background: farbe } as CSSProperties} />
                <span className="pl-tag-zeit">{hhmm(t.start_at)}<small>{hhmm(t.end_at)}</small></span>
                <span className="pl-tag-inhalt">
                  <span className="pl-tag-was">{istBesichtigung(t) ? "👁 " : ""}{t.projekt_title || t.title || "Termin"}</span>
                  <span className="pl-tag-wer">{[t.kunde_name, leute].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="pl-tag-pfeil" aria-hidden="true">›</span>
              </button>
              <div className="pl-termin-legen">
                <button type="button" onClick={() => onAufnehmen(t, "verschieben")}>⤳ Verschieben</button>
                <button type="button" onClick={() => onAufnehmen(t, "kopieren")}>⧉ Kopieren</button>
              </div>
            </div>
          );
        })}
        {termine.length === 0 && <div className="pl-tag-leer">Kein Einsatz an diesem Tag.</div>}
      </div>

      <AboTerminListe dateIso={dateIso} {...abo} onAbo={onAbo} />
    </section>
  );
}
